import React, { useState } from "react";
import { toast } from "sonner";
import { CreditCard, Lock, ShieldCheck, Tag, CheckCircle2, X } from "lucide-react";
import api from "@/lib/api";

/**
 * Phase 4 Stripe checkout launcher. Replaces the Phase 3 DEV button on
 * customer-facing surfaces. Creates a Payment (status=pending) + a Stripe
 * Checkout Session, then redirects to Stripe. On return, `/payment/success`
 * polls `/payments/status/{session_id}` and shows a receipt.
 *
 * Phase 6+7: adds an optional promo code input (booking/order/consultation/
 * milestone only). Validation happens via /promotions/check for a live
 * discount preview; the code is then passed to /payments/checkout so the
 * final Stripe amount matches the preview.
 */
export default function StripeCheckoutPanel({
  purpose,           // "booking" | "order" | "milestone" | "tip" | "featured_placement" | "consultation"
  targetId,
  milestoneId,
  tipAmount,
  amount,            // display amount (server always recomputes)
  fee_snapshot,      // optional preview; if present we render the breakdown
  categoryId,
  providerId,
  ctaLabel = "Continue to secure payment",
  onCreated,         // optional callback (payment) after successful session creation
  testId = "stripe-checkout-panel",
}) {
  const [loading, setLoading] = useState(false);
  const [promoInput, setPromoInput] = useState("");
  const [promo, setPromo] = useState(null);        // { code, discount, kind }
  const [validating, setValidating] = useState(false);

  const promoEligible = ["booking", "order", "consultation", "milestone"].includes(purpose);
  const previewSubtotal = fee_snapshot?.subtotal ?? Number(amount) ?? 0;
  const discount = promo?.discount || 0;
  const waivesServiceFee = promo?.kind === "free_service_fee";

  const applyPromo = async () => {
    const code = (promoInput || "").trim();
    if (!code) return;
    setValidating(true);
    try {
      const { data } = await api.post("/promotions/check", {
        code,
        subtotal: previewSubtotal,
        category_id: categoryId || null,
        provider_id: providerId || null,
      });
      setPromo({ code: data.promo?.code || code, discount: Number(data.discount || 0), kind: data.kind });
      toast.success(
        data.kind === "free_service_fee"
          ? `Promo ${data.promo?.code} applied — service fee waived`
          : `Promo ${data.promo?.code} applied — $${Number(data.discount || 0).toFixed(2)} off`
      );
    } catch (err) {
      setPromo(null);
      toast.error(err?.response?.data?.error || "Invalid promo code");
    } finally {
      setValidating(false);
    }
  };

  const removePromo = () => {
    setPromo(null);
    setPromoInput("");
  };

  const proceed = async () => {
    setLoading(true);
    try {
      const payload = {
        purpose,
        target_id: targetId,
        origin_url: window.location.origin,
      };
      if (milestoneId) payload.milestone_id = milestoneId;
      if (tipAmount) payload.tip_amount = Number(tipAmount);
      if (promo?.code) payload.promo_code = promo.code;
      const { data } = await api.post("/payments/checkout", payload);
      onCreated?.(data);
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err?.response?.data?.error || "Could not start checkout");
      setLoading(false);
    }
  };
  return (
    <div className="rounded-2xl border border-[#165a71]/30 bg-gradient-to-br from-[#165a71]/5 to-white p-5" data-testid={testId}>
      <div className="flex items-start gap-3">
        <div className="mt-0.5 h-9 w-9 shrink-0 rounded-xl bg-[#165a71] text-white flex items-center justify-center">
          <CreditCard className="h-4 w-4" />
        </div>
        <div className="flex-1">
          <div className="font-display text-base font-semibold text-[#071b22]">Secure payment</div>
          <div className="text-sm text-[#252A34]/75 mt-0.5">
            Provider payout is released according to the service completion policy.
          </div>
          {fee_snapshot && (
            <div className="mt-3 rounded-xl bg-white border border-slate-200 p-3 text-sm space-y-1" data-testid="fee-breakdown">
              <Row label="Subtotal" value={`$${fee_snapshot.subtotal.toFixed(2)}`} />
              {discount > 0 && (
                <Row label={`Promo ${promo.code}`} value={`−$${discount.toFixed(2)}`} accent />
              )}
              {(fee_snapshot.customer_service_fee_amount > 0 && !waivesServiceFee) && (
                <Row label={`Service fee (${fee_snapshot.customer_service_fee_pct}%)`} value={`+$${fee_snapshot.customer_service_fee_amount.toFixed(2)}`} />
              )}
              {waivesServiceFee && (
                <Row label="Service fee (waived)" value="$0.00" accent />
              )}
              {fee_snapshot.tip_amount > 0 && (
                <Row label="Tip" value={`+$${fee_snapshot.tip_amount.toFixed(2)}`} />
              )}
              <div className="border-t border-slate-100 pt-1 mt-1 flex items-center justify-between text-[#071b22]">
                <span className="font-medium">Estimated total</span>
                <span className="font-display font-semibold">
                  ${Math.max(0, (fee_snapshot.total_charge || 0) - discount - (waivesServiceFee ? (fee_snapshot.customer_service_fee_amount || 0) : 0)).toFixed(2)}
                </span>
              </div>
              <div className="text-[10px] text-[#252A34]/50 pt-1">Server recomputes the final Stripe amount — this is a preview only.</div>
            </div>
          )}

          {promoEligible && (
            <div className="mt-3" data-testid="promo-code-row">
              {!promo ? (
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#252A34]/50" />
                    <input
                      value={promoInput}
                      onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                      placeholder="Promo code"
                      className="w-full rounded-full border border-slate-200 bg-white pl-8 pr-3 py-2 text-sm uppercase tracking-wider focus:border-[#165a71] focus:outline-none"
                      data-testid="promo-code-input"
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          applyPromo();
                        }
                      }}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={applyPromo}
                    disabled={validating || !promoInput.trim()}
                    className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium disabled:opacity-50 hover:bg-slate-50"
                    data-testid="promo-code-apply"
                  >
                    {validating ? "Checking…" : "Apply"}
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between rounded-full bg-emerald-50 border border-emerald-200 pl-3 pr-1 py-1 text-xs" data-testid="promo-code-applied">
                  <span className="inline-flex items-center gap-1.5 text-emerald-800 font-medium">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {promo.code} applied
                    {promo.kind === "free_service_fee" ? " · service fee waived" : ` · −$${discount.toFixed(2)}`}
                  </span>
                  <button
                    type="button"
                    onClick={removePromo}
                    className="rounded-full p-1 hover:bg-emerald-100"
                    aria-label="Remove promo"
                    data-testid="promo-code-remove"
                  >
                    <X className="h-3.5 w-3.5 text-emerald-700" />
                  </button>
                </div>
              )}
            </div>
          )}

          <button
            onClick={proceed}
            disabled={loading}
            data-testid="stripe-checkout-btn"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#11485a] disabled:opacity-60"
          >
            <Lock className="h-4 w-4" /> {loading ? "Opening secure checkout…" : ctaLabel}
            {amount ? ` · $${Number(amount).toFixed(2)}` : ""}
          </button>
          <div className="mt-3 flex items-center gap-2 text-[11px] text-[#252A34]/60">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0" />
            <span>Powered by Stripe · Test mode · Use card 4242 4242 4242 4242 (any future expiry, any CVC).</span>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value, accent }) => (
  <div className={`flex items-center justify-between ${accent ? "text-emerald-700" : "text-[#252A34]/85"}`}>
    <span>{label}</span>
    <span className="font-medium">{value}</span>
  </div>
);
